import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PropertyCard from '../components/PropertyCard';
import { ArrowRight, Building2, TrendingUp, Shield, Award, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHeroDropdown, setShowHeroDropdown] = useState(false);
  const [showBottomDropdown, setShowBottomDropdown] = useState(false);

  const propertyCategories = [
    { name: 'Todas las Propiedades', path: '/propiedades' },
    { name: 'Residenciales', path: '/propiedades?category=residential' },
    { name: 'Comerciales', path: '/propiedades?category=commercial' },
    { name: 'Terrenos', path: '/propiedades?category=land' },
  ];

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`${API}/properties`);
        const featured = response.data.filter(p => p.featured);
        setFeaturedProperties(featured);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="animate-fade-in">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Tu Patrimonio
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-golden-400 to-golden-600">
                Nuestra Misión
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Invierte en tu futuro con las mejores propiedades de México. Desde casas residenciales hasta hoteles boutique.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* Ver Propiedades Dropdown - Solo se abre con clic */}
              <div className="relative">
                <Button
                  onClick={() => setShowHeroDropdown(!showHeroDropdown)}
                  size="lg"
                  className="bg-golden-600 hover:bg-golden-700 text-white text-lg px-8 py-6 h-auto w-full sm:w-auto"
                >
                  Ver Propiedades
                  <ChevronDown className={`ml-2 w-5 h-5 transition-transform ${showHeroDropdown ? 'rotate-180' : ''}`} />
                </Button>
                
                {showHeroDropdown && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 z-50">
                    {propertyCategories.map((cat) => (
                      <Link
                        key={cat.path}
                        to={cat.path}
                        onClick={() => setShowHeroDropdown(false)}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-golden-50 hover:text-golden-600 transition-colors font-medium"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              <Button asChild size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 text-lg px-8 py-6 h-auto">
                <Link to="/contacto">Agendar Cita</Link>
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-golden-500 mb-2">500+</div>
              <div className="text-sm md:text-base text-gray-400">Propiedades Vendidas</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-golden-500 mb-2">15+</div>
              <div className="text-sm md:text-base text-gray-400">Años de Experiencia</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-golden-500 mb-2">98%</div>
              <div className="text-sm md:text-base text-gray-400">Clientes Satisfechos</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-golden-500 mb-2">24/7</div>
              <div className="text-sm md:text-base text-gray-400">Atención al Cliente</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Propiedades Destacadas
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Descubre nuestra selección exclusiva de propiedades premium en las mejores ubicaciones
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Cargando propiedades...</p>
            </div>
          ) : featuredProperties.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {featuredProperties.map((property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </div>
              <div className="text-center">
                {/* Ver Todas las Propiedades Dropdown - Solo se abre con clic */}
                <div className="inline-block relative">
                  <Button
                    onClick={() => setShowBottomDropdown(!showBottomDropdown)}
                    size="lg"
                    className="bg-golden-600 hover:bg-golden-700 text-white"
                  >
                    Ver Todas las Propiedades
                    <ChevronDown className={`ml-2 w-5 h-5 transition-transform ${showBottomDropdown ? 'rotate-180' : ''}`} />
                  </Button>
                  
                  {showBottomDropdown && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-64 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 z-50">
                      {propertyCategories.map((cat) => (
                        <Link
                          key={cat.path}
                          to={cat.path}
                          onClick={() => setShowBottomDropdown(false)}
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-golden-50 hover:text-golden-600 transition-colors font-medium"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No hay propiedades disponibles en este momento.</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              ¿Por Qué Elegirnos?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Somos tu socio confiable en cada paso del camino hacia tu nueva propiedad
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-golden-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-golden-600 transition-colors">
                <Building2 className="w-10 h-10 text-golden-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Portafolio Exclusivo
              </h3>
              <p className="text-gray-600">
                Propiedades cuidadosamente seleccionadas en las mejores ubicaciones de México
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-golden-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-golden-600 transition-colors">
                <TrendingUp className="w-10 h-10 text-golden-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Inversión Inteligente
              </h3>
              <p className="text-gray-600">
                Asesoría experta para maximizar el retorno de tu inversión inmobiliaria
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-golden-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-golden-600 transition-colors">
                <Shield className="w-10 h-10 text-golden-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Proceso Seguro
              </h3>
              <p className="text-gray-600">
                Transacciones transparentes y seguras con respaldo legal completo
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-golden-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-golden-600 transition-colors">
                <Award className="w-10 h-10 text-golden-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Atención Premium
              </h3>
              <p className="text-gray-600">
                Servicio personalizado 24/7 para acompañarte en todo el proceso
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-golden-600 to-golden-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,white,transparent_50%)]" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para Invertir en tu Futuro?
          </h2>
          <p className="text-xl text-golden-50 mb-8">
            Aparta tu propiedad ideal con solo $15,000 MXN o el 5% de enganche. Agenda una cita con nuestros expertos.
          </p>
          <Button asChild size="lg" className="bg-white text-golden-600 hover:bg-gray-100 text-lg px-8 py-6 h-auto">
            <Link to="/contacto">
              Agenda tu Cita Ahora
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
